/** Shared maze play shell while route content or session bootstrap is still loading (demo and live). */
import { MazePlayAreaSkeleton } from '@/components/molecules/maze-play-area-skeleton';
import { MazePlayTopGradient } from '@/components/molecules/maze-play-top-gradient';
import {
  LeftHudLabelSkeleton,
  MazeHud,
  TimerChipSkeleton,
  type MazeHudProps,
} from '@/components/organisms/maze-hud';
import { useDemoStore } from '@/store/demo';
import { useLiveStore } from '@/store/live';
import { usePreviewStore } from '@/store/preview';
import { useSettingsStore } from '@/store/settings';
import { APP_COLOR } from '@/theme/color';
import {
  isMazePlayModeDemo,
  isMazePlayModePreview,
  MazePlayMode,
} from '@/utils/maze/maze-play-mode';
import { paths } from '@app/router/routes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigate } from '@/hooks/use-app-navigate';
import { MazeControls } from '../organisms/maze-controls';

export type LiveGameRouteSkeletonProps = {
  readonly interactiveHud?: boolean;
} & Partial<MazeHudProps>;

interface PlayModeLevelTimerSnapshot {
  readonly isDemo: boolean;
  readonly isPreview: boolean;
  readonly demoLevel: number;
  readonly previewLevel: number;
  readonly liveLevel: number;
  readonly demoTimer: number;
  readonly previewTimer: number;
  readonly liveTimer: number;
}

function resolvePlayModeLevelTimer(snapshot: PlayModeLevelTimerSnapshot): {
  level: number;
  timer: number;
} {
  const {
    isDemo,
    isPreview,
    demoLevel,
    previewLevel,
    liveLevel,
    demoTimer,
    previewTimer,
    liveTimer,
  } = snapshot;
  if (isDemo) {
    return { level: demoLevel, timer: demoTimer };
  }
  if (isPreview) {
    return { level: previewLevel, timer: previewTimer };
  }
  return { level: liveLevel, timer: liveTimer };
}

function StaticHudSkeleton({ playMode }: Readonly<{ playMode?: MazePlayMode }>) {
  const isDemo = playMode !== undefined && isMazePlayModeDemo(playMode);
  const isPreview = playMode !== undefined && isMazePlayModePreview(playMode);

  return (
    <div className="relative z-20 mb-1 grid w-full max-w-md grid-cols-[1fr_auto_1fr] items-center gap-2 py-1">
      <div className="min-w-0 justify-self-start pr-2">
        <LeftHudLabelSkeleton />
      </div>
      <div className="justify-self-center">
        {!isDemo && !isPreview ? <TimerChipSkeleton /> : null}
      </div>
      <div className="flex justify-end justify-self-end">
        <div
          className="h-11 w-11 shrink-0 animate-pulse rounded-full border"
          style={{
            borderColor: APP_COLOR.accentSoft40,
            background: 'var(--maze-hud-pill-bg, rgba(255, 255, 255, 0.12))',
          }}
          aria-hidden
        />
      </div>
    </div>
  );
}

export function LiveGameRouteSkeleton(props: LiveGameRouteSkeletonProps = {}) {
  const { t } = useTranslation();
  const demoLevel = useDemoStore((s) => s.level);
  const demoTimer = useDemoStore((s) => s.timer);
  const liveLevel = useLiveStore((s) => s.level);
  const liveTimer = useLiveStore((s) => s.timer);
  const previewLevel = usePreviewStore((s) => s.level);
  const previewTimer = usePreviewStore((s) => s.timer);
  const soundEffectsEnabled = useSettingsStore((s) => s.soundEffectsEnabled);
  const controlsEnabled = useSettingsStore((s) => s.controlsEnabled);

  const hudProps = useMemo((): MazeHudProps | null => {
    if (
      !props.interactiveHud ||
      props.playMode === undefined ||
      props.onToggleSoundEffects === undefined ||
      props.onToggleControls === undefined ||
      props.onStartFresh === undefined
    ) {
      return null;
    }

    const isDemo = isMazePlayModeDemo(props.playMode);
    const isPreview = props.playMode === MazePlayMode.Preview;

    const { level, timer } = resolvePlayModeLevelTimer({
      isDemo,
      isPreview,
      demoLevel,
      previewLevel,
      liveLevel,
      demoTimer,
      previewTimer,
      liveTimer,
    });

    return {
      playMode: props.playMode,
      level: props.level ?? level,
      timer: props.timer ?? timer,
      soundEffectsEnabled: props.soundEffectsEnabled ?? soundEffectsEnabled,
      controlsEnabled: props.controlsEnabled ?? controlsEnabled,
      onToggleSoundEffects: props.onToggleSoundEffects,
      onToggleControls: props.onToggleControls,
      onStartFresh: props.onStartFresh,
      isStartFreshPending: props.isStartFreshPending,
      onDone: props.onDone,
      isMetricsLoading: props.isMetricsLoading ?? true,
    };
  }, [
    demoLevel,
    demoTimer,
    controlsEnabled,
    liveLevel,
    liveTimer,
    previewLevel,
    previewTimer,
    props,
    soundEffectsEnabled,
  ]);

  return (
    <main
      aria-busy="true"
      aria-live="polite"
      className="relative min-h-dvh w-full overflow-x-hidden py-[32px]"
    >
      <MazePlayTopGradient />
      <span className="sr-only">{t('loading.game')}</span>

      <div className="mx-auto flex w-full max-w-[600px] h-[calc(100dvh_-_64px)] flex-col items-center justify-between gap-2 px-[15px]">
        {hudProps ? <MazeHud {...hudProps} /> : <StaticHudSkeleton />}

        <div className="relative h-[50vh] w-full max-w-md min-h-0 shrink-0 overflow-hidden">
          <div className="absolute inset-0 flex min-h-0 items-center justify-center">
            <MazePlayAreaSkeleton />
          </div>
        </div>

        <div className="relative pointer-events-none flex w-full shrink-0 flex-col items-center opacity-90 pt-[55px]">
          <MazeControls isVisible={controlsEnabled} />
          <p
            className="absolute left-1/2 bottom-[120px] z-16 max-w-[calc(100%-34px)] overflow-hidden whitespace-nowrap rounded-full border px-[24px] py-[10px] text-center text-[12px] font-bold capitalize tracking-[0.16px] text-ellipsis pointer-events-none -translate-x-1/2"
            style={{
              color: APP_COLOR.accentSoft40,
              visibility: controlsEnabled ? 'visible' : 'hidden',
              opacity: controlsEnabled ? 1 : 0,
            }}
            aria-hidden={!controlsEnabled}
          >
            {t('controls.hint')}
          </p>
        </div>
      </div>
    </main>
  );
}

/** Suspense / demo bootstrap fallback with working settings button and menu. */
export function LiveGameRouteSkeletonWithHud({
  playMode,
  onDone,
}: Readonly<{
  playMode: MazePlayMode;
  onDone?: () => void;
}>) {
  const navigate = useAppNavigate();
  const soundEffectsEnabled = useSettingsStore((s) => s.soundEffectsEnabled);
  const controlsEnabled = useSettingsStore((s) => s.controlsEnabled);
  const setSoundEffectsEnabled = useSettingsStore((s) => s.setSoundEffectsEnabled);
  const setControlsEnabled = useSettingsStore((s) => s.setControlsEnabled);
  const demoLevel = useDemoStore((s) => s.level);
  const demoTimer = useDemoStore((s) => s.timer);
  const liveLevel = useLiveStore((s) => s.level);
  const liveTimer = useLiveStore((s) => s.timer);
  const previewLevel = usePreviewStore((s) => s.level);
  const previewTimer = usePreviewStore((s) => s.timer);
  const isDemo = isMazePlayModeDemo(playMode);
  const isPreview = playMode === MazePlayMode.Preview;

  const { level, timer } = resolvePlayModeLevelTimer({
    isDemo,
    isPreview,
    demoLevel,
    previewLevel,
    liveLevel,
    demoTimer,
    previewTimer,
    liveTimer,
  });

  const navigateHome = () => {
    navigate(paths.home, { replace: true });
  };

  return (
    <LiveGameRouteSkeleton
      interactiveHud
      playMode={playMode}
      level={level}
      timer={timer}
      soundEffectsEnabled={soundEffectsEnabled}
      controlsEnabled={controlsEnabled}
      onToggleSoundEffects={setSoundEffectsEnabled}
      onToggleControls={setControlsEnabled}
      onStartFresh={navigateHome}
      onDone={onDone ?? (isDemo || isPreview ? navigateHome : undefined)}
    />
  );
}
